<template>
  <!-- `data-nav-header` 는 renewal 의 nav-menu.js 가 "패널이 열렸을 때 배경을 맞출 헤더"를
       찾는 표식이다. renewal 단독 사이트에서는 `.header` 를 찾지만 여기선 우리 헤더다. -->
  <header ref="headerEl" class="headerMain" :class="{ 'locale-pending': !localeReady }" data-nav-header>
    <div class="container flex" @click.stop="isShowMobileMenu = false">
      <div class="leftMenu flex">
        <NuxtLink to="/" class="headerLogo">
          <img src="~/assets/images/logo_blue.svg" alt="codepresso's logo image">
        </NuxtLink>
        <!-- 상단 메뉴와 드롭다운은 renewal 것을 그대로 쓴다(마크업·클래스 무수정).
             `.renewal-root` 안에서만 renewal CSS 가 살아나므로 헤더 나머지에는 영향이 없다. -->
        <!-- 1080px 이하에서는 이 자리에 두지 않는다 — 같은 SiteNav 를 모바일 드로어가
             렌더하므로, 둘 다 마운트하면 nav-panel id 가 중복된다(aria-controls 가 깨진다). -->
        <div v-if="!isMobileNav" class="headerRenewalNav renewal-root" @click="onClickRenewalLink">
          <SiteNav />
        </div>
      </div>
      <div class="rightMenu flex">
        <ul class="btnArea flex">
          <LazyUserCommonCreditInfo v-if="isAdmin" :is-main="true" />
          <!-- Auth-state CTAs render only once `getUserData()` has resolved,
               to avoid the OAuth-callback flash where the header briefly shows
               "Sign in" before flipping to "Dashboard" once userStore is
               populated. Header may render before auth resolves on public
               marketing routes (Home / Policy) per app.vue's isReady gate. -->
          <template v-if="userStore.isAuthResolved">
            <li>
              <NuxtLink v-if="!isLoginUser" to="/sign" class="btn whiteLineBtn">
                {{ t("button.sign") }}
              </NuxtLink>
              <NuxtLink v-if="isLoginUser" to="/dashboard" class="btn indigoBtn">
                {{ t("common-dashboard") }}
              </NuxtLink>
            </li>
            <li v-if="!isLoginUser">
              <NuxtLink to="/home/request/demo" class="btn blueBtn">
                {{ t("button.demo") }}
              </NuxtLink>
            </li>
            <li v-else ref="profileArea" class="profileBtn" @click.stop="onToggleProfile">
              <LazyUserCommonProfileButton :is-profile-open-flag="isProfileOpenFlag" />
            </li>
          </template>
        </ul>
        <button
          type="button"
          class="ham"
          :class="{on: isShowMobileMenu}"
          @click.stop="methods.onClickMobileMenuButton">
          <span></span>
        </button>
      </div>
      <LazyHomeHeaderMobile
        :class="{ on: isShowMobileMenu}"
        :use-renewal-nav="isMobileNav"
        :menu-list="menuList"
        :is-login-user="isLoginUser"
        @on-click-menu="methods.onClickMenu"
        @on-click-sub-menu="methods.onClickSubMenu"
        @reset-menu-list="methods.resetMenuList"
      />
      <div
        class="navBg"
        :class="{ on: isShowMobileMenu }"
        @click.stop="isShowMobileMenu = false" />
    </div>
  </header>
</template>

<script setup lang="ts">
import {nextTick, onMounted, ref, useRoute, watch} from '#imports';
import {useI18n} from "vue-i18n";
import type {PropType} from "vue";
import {computed} from 'vue';
import type {HomeMenuType} from "~~/package/config/HomeSubMenuConfig";
import {onClickOutside, useMediaQuery, useMounted} from "@vueuse/core";
import {useUserStore} from "~~/application/store/user/userStore";
import SiteNav from "~~/codepresso-renewal/components/SiteNav.vue";
import {useRenewalLinkInterception} from "~/composables/useRenewalLinkInterception";
// GNB 전용 스코프 CSS. 모든 셀렉터가 `.renewal-root` 를 요구하므로 헤더 나머지에는 닿지 않는다.
import "~~/codepresso-renewal/embed/css/nav.css";

const {t} = useI18n({useScope: 'local'})
// renewal GNB 의 링크는 평범한 <a href> 다. DOM 을 고치지 않고 클릭만 가로채
// SPA 내비게이션으로 바꾼다.
const onClickRenewalLink = useRenewalLinkInterception();

// GNB 를 세로 드로어로 접는 기준. renewal 의 드로어 CSS(`css/components/ui/nav-mobile.css`)와
// 메뉴 동작(`js/nav-menu.js`)이 같은 값(1080px)에서 세로로 바뀌므로 여기서도 그 값을 쓴다 —
// 어긋나면 그 구간에서 가로 메뉴가 세로 드로어 안에 들어가 깨진다. 우리 헤더(_header.scss)가
// 햄버거로 넘어가는 값도 1080px 이다. 서버에서는 false 라 프리렌더에 데스크톱 GNB 가 실린다.
//
// 실제 폭은 마운트 뒤에 반영한다. useMediaQuery 는 클라이언트 setup 에서 바로 실제 값을 돌려주므로,
// 1080px 이하로 처음 열면 첫 렌더가 서버 HTML(데스크톱 GNB)과 달라 Vue 가 헤더 메뉴를 버리고 다시 만들고
// "Hydration completed but contains mismatches" 오류를 남겼다(Faro 로 올라간다). 마운트 전 그 폭의
// 데스크톱 GNB 는 _header.scss 가 가리므로 보이지 않는다.
const matchesMobileNav = useMediaQuery('(max-width: 1080px)');
const isMounted = useMounted();
const isMobileNav = computed(() => isMounted.value && matchesMobileNav.value);
const props = defineProps({
  menuList: { type: Array as PropType<HomeMenuType[]>, default: () => [] },
  isLoginUser: { type: Boolean, default: false }
});
const emit = defineEmits(["onClickMenu", "onClickSubMenu", "resetMenuList"]);
const route = useRoute();
const userStore = useUserStore();

// Hide the header until after Vue mounts on the client and nuxt-i18n has
// applied the cookie-detected locale. Otherwise the static prerender (default
// locale = 'en') flashes English menu items / button labels before they swap
// to the user's actual locale (ko/ja/...). Pure visibility — layout space
// is reserved (no CLS).
const localeReady = ref(false);
onMounted(() => { nextTick(() => { localeReady.value = true; }); });

const isShowMobileMenu = ref(false);
const isProfileOpenFlag = ref(false);
const profileArea = ref(null);

const isAdmin = computed(() =>
  userStore.isAdminType || userStore.isSuperAdminType
)

const onToggleProfile = () => {
  isShowMobileMenu.value = false;
  isProfileOpenFlag.value = !isProfileOpenFlag.value;
}

const methods = {
  onClickMenu(menuTitle: string) {
    emit("onClickMenu", menuTitle, "header");
  },
  onClickSubMenu(subMenuTitle: string) {
    emit("onClickSubMenu", subMenuTitle);
  },
  resetMenuList() {
    emit("resetMenuList");
  },
  onClickMobileMenuButton() {
    isShowMobileMenu.value = !isShowMobileMenu.value;
  }
};


watch(route, () => {
  // 이동하면 모바일 드로어를 닫는다. SPA 이동은 DOM 을 다시 만들지 않아 열린 채로 남는다.
  isShowMobileMenu.value = false;

  if (route.path === "/") {
    methods.resetMenuList();
  } else {
    const strSplit = route.path.split("/");
    methods.onClickSubMenu(strSplit[2] === undefined ? "undefined" : strSplit[2]);
  }
}, { immediate: true });

onClickOutside(profileArea, () => {
  isProfileOpenFlag.value = false;
});

// 드로어는 헤더 바로 아래에서 시작하는데(_header.scss: top: calc(100% + 1px)), 높이는 헤더가 화면 맨 위에
// 있다고 보고 100vh - 64px 로 잡혀 있다. 관리자 저잔액 배너가 헤더 위에 얹히면 드로어가 그만큼 화면 아래로
// 넘쳐 마지막 줄(대시보드 버튼)이 잘렸다. 열 때 드로어가 실제로 시작하는 위치를 재 남은 높이만 쓰게 한다.
const headerEl = ref<HTMLElement | null>(null);
watch(isShowMobileMenu, async (open) => {
  if (!open) return;
  await nextTick();
  const drawer = headerEl.value?.querySelector<HTMLElement>('.navMain.mobile');
  if (drawer) drawer.style.setProperty('--mobile-nav-top', `${Math.round(drawer.getBoundingClientRect().top)}px`);
});
</script>

<!-- 상단 메뉴 문구는 여기 없다 — 데스크톱 GNB 는 renewal 의 SiteNav 가 자기 사전으로
     렌더하고, 모바일 메뉴는 HomeHeaderMobile 이 자기 <i18n> 을 쓴다. -->
<i18n>
{
  "en": {
    "button": {
      "sign": "Log In",
      "demo": "Request a Demo"
    }
  },
  "ko": {
    "button": {
      "sign": "로그인",
      "dashboard": "대시보드",
      "demo": "무료 체험 신청"
    }
  },
  "ja": {
    "button": {
      "sign": "ログイン",
      "dashboard": "ダッシュボード",
      "demo": "デモをリクエスト"
    }
  }
}
</i18n>
<style lang="scss">
@import "~/assets/scss/abstracts/variables.scss";
@import "~/assets/scss/abstracts/mixin.scss";
@import "~/assets/scss/header.scss";

// Suppress the static-prerender locale flash. Set in script setup via `nextTick(() => localeReady.value = true)`.
// Uses `visibility` so layout space is preserved (no CLS), only text content is hidden.
//
// 가리는 것은 **언어가 바뀔 방문자에게만** 필요하다. 프리렌더는 기본 언어(en)로 구워지고,
// 헤더는 Lazy 라 문서가 공개된 뒤에야 마운트되므로 그 사이 영어 메뉴가 스쳐 보인다.
// 언어가 프리렌더와 같으면 바뀔 글자가 없어 가릴 이유도 없다. 예전엔 조건 없이 가려서
// 헤더가 hydration 완료까지 숨겨졌고, renewal 콘텐츠가 커진 뒤로는 그게 약 1.7초였다.
// `data-locale-swap` 은 인라인 anti-FOUC 스크립트(package/config/i18nAntiFouc.ts — nuxt.config.ts 가
// head 에 싣는다)가 첫 페인트 전에 달아 두고 지우지 않는다.
html[data-locale-swap] .headerMain.locale-pending { visibility: hidden; }

// 모바일 드로어 높이 — 열 때 잰 드로어 시작 위치(--mobile-nav-top, script 의 watch 참고)를 뺀 만큼만 쓴다.
// 재기 전에는 _header.scss 와 같은 64px 이다. 관리자 저잔액 배너가 헤더 위에 얹히면 64px 로는 넘쳤다.
// 높이는 곧바로 바꾼다 — _header.scss 의 `transition: all` 그대로면 페이드인 동안 높이도 줄어 아래 버튼이 미끄러져 올라온다.
.headerMain .navMain.mobile {
  height: calc(100vh - var(--mobile-nav-top, 64px));
  transition: all 0.3s ease-in-out, height 0s;
}

// ─── renewal GNB ────────────────────────────────────────────────────────────
// 상단 메뉴와 메가패널은 renewal 것을 그대로 쓴다. renewal 의 CSS·DOM 은 고치지 않고,
// 여기서는 우리 헤더 쪽 사정(자리·모바일 분기·열림 상태)만 맞춘다.
// 메뉴 자체의 CSS(embed/css/nav.css)는 script setup 에서 import 한다 — Sass 를 거치면
// `.css` 확장자가 plain @import 로 남아 경로 해석이 어긋난다.

.headerMain {
  // 상단 메뉴 줄의 생김새는 우리 헤더 것이다(요구 1·3). renewal 에서 가져오는 것은
  // 메뉴 항목과 hover 시 열리는 메가패널이고, 글자·간격·색·활성 표시는 기존 `.navMain`
  // 값을 그대로 쓴다. 아래 값은 교체 전 화면에서 실측한 것이다:
  //   ul gap 48px (≤1240px 24px) / 16px·500·#000 / li padding 5px 0 / 활성 rgb(26,97,234)
  .headerRenewalNav {
    flex: 1;

    // renewal 은 이 값으로 항목(.nav-item) 높이를 헤더 높이만큼 채운다 — 메뉴에서 패널로
    // 옮길 때 hover 가 끊기지 않게. 우리 헤더 높이(.container 80px)와 맞춘다.
    --header-height: 80px;

    .nav-list {
      // renewal 은 항목마다 좌우 24px 여백을 주고, 첫 항목의 왼쪽 여백을 이 음수 margin 으로
      // 상쇄한다. 우리는 항목 여백을 없애고(.nav-item) gap 으로 간격을 주므로 상쇄도 걷는다 —
      // 남겨 두면 메뉴 전체가 24px 당겨져 로고↔첫 메뉴가 메뉴 사이보다 24px 좁았다
      // (1240px 이하에서는 0px 로 로고에 붙었다, REQ-399). 이제 로고↔첫 메뉴는 `.leftMenu`
      // gap 이 정하고, 그 값은 메뉴 사이 gap 과 같다(48px / ≤1240px 24px).
      margin-inline-start: 0;
      gap: 48px;
      @include mq(screen-1240) {
        gap: 24px;
      }
    }

    .nav-item {
      @include paddingBox(5px 0);
      // 패널의 위치 기준을 헤더로 넘긴다 — 아래 .nav-panel 참고.
      position: static;
    }

    // 메가패널은 뷰포트가 아니라 헤더 바로 아래에 붙인다(REQ-397).
    // renewal 은 패널을 `position: fixed; top: var(--header-height)` 로 둔다 — 단독 사이트의
    // 헤더는 화면 맨 위에 붙어 있어 "뷰포트 80px = 헤더 아래" 가 늘 맞는다. 우리 헤더 위에는
    // 관리자에게 잔여 크레딧 배너(UserCommonCreditBanner)가 얹혀 헤더가 그만큼 내려가는데,
    // 패널은 그대로 80px 에서 열려 로고·메뉴·버튼을 덮었다.
    // 기준을 헤더(.headerMain — sticky 라 위치 기준 상자가 된다)로 옮기면 배너·헤더 높이와
    // 상관없이 헤더 바로 아래에서 열린다. 너비(inset-inline: 0)는 헤더 전체 폭 그대로다.
    .nav-panel {
      position: absolute;
      inset-block-start: 100%;
    }

    .nav-trigger,
    .nav-link {
      gap: 6px; // 기존 `.menuTit .arrow { margin-left: 6px }`
      font-size: 16px;
      font-weight: 500;
      line-height: 1.12;
      letter-spacing: normal;
      color: #000;
      transition: all 0.1s ease-in-out;
      // renewal mobile.css 의 태블릿 구간(1081~1200px)은 메뉴 글자를 한 줄로 묶는다(`nowrap`) —
      // 로고가 작고 로그인이 접히는 renewal 단독 헤더 기준이다. 우리 상단 바는 로고·로그인·데모
      // 버튼을 그대로 두므로, 묶이면 en·es·vi 에서 상단 바가 화면보다 넓어져 가로로 넘쳤다.
      // renewal 페이지에서만 실리는 CSS 라 그 페이지(또는 거기서 SPA 이동한 뒤)에서만 났다.
      white-space: normal;
    }

    // 셰브론은 글자와 같은 색으로 따라간다(기존 arrow 의 path fill 이 글자색과 같았다).
    .nav-chevron {
      min-width: 16px;
      width: 16px;
      height: 16px;
      color: inherit;
    }

    // hover 와 "지금 보고 있는 메뉴" 를 같은 파란색으로. 활성 표시는 renewal SiteNav 가
    // 현재 경로를 보고 `is-active` 를 붙여 주고, 색은 여기서 정한다.
    .nav-item:hover > .nav-trigger,
    .nav-item.is-active > .nav-trigger,
    .nav-link:hover,
    .nav-link.is-active {
      color: $color-main;
    }
  }

  // 메가패널이 열려 있는 동안 헤더 배경을 패널과 같은 흰 서피스로 맞춘다.
  // (renewal 의 nav-menu.js 가 [data-nav-header] 에 붙이는 클래스)
  // 1081px 이상에서만 — nav-menu.js 는 1080px 이하 드로어에서 아코디언을 펼 때도 이 클래스를 다는데,
  // 그때는 패널이 헤더 아래 붙지 않으므로 헤더 모양을 바꿀 이유가 없다(바꾸면 헤더 아래 구분선이 사라졌다).
  @media (min-width: 1081px) {
    &.is-nav-open {
      background: #fff;
      border-bottom-color: transparent;
    }
  }
}

// ─── 다크 페이지에서의 헤더 ──────────────────────────────────────────────────
// AXpresso 계열 페이지(/axpresso, /ax-build, /ax-grow)는 renewal 이 <main> 에
// `main-dark` 를 붙여 짙은 남색 배경으로 그린다. 그 위에 밝은 헤더가 얹히면 헤더만
// 동떨어져 보이므로, 페이지 맨 윗부분과 같은 색으로 맞추고 글자를 흰색으로 뒤집는다.
//
// 어떤 페이지가 다크인지는 renewal 이 정한다 — 목록을 여기 복사해 두면 renewal 이
// 페이지를 추가·변경할 때 어긋난다. `:has()` 로 "지금 콘텐츠가 다크인가"를 직접 본다.
// 값은 renewal 토큰과 같다: 배경 --color-axpresso-bg-1(#050C21), 글자 --color-surface.

// 다크 위에서는 기존 포인트 블루($color-main)가 묻힌다. 밝은 쪽으로 올린 색 —
// 메뉴의 hover·현재 페이지 표시와 관리자 크레딧의 숫자가 같이 쓴다.
$dark-header-accent: color-mix(in srgb, $color-main 45%, white);
// 헤더가 면을 깔 때의 색 — 페이지 맨 윗부분(--color-axpresso-bg-1)과 같다.
$dark-header-bg: #050C21;

#wrapper:has(.renewal-page .main-dark) {
  .headerMain {
    // 맨 위에서는 배경을 깔지 않는다. 기존 헤더가 그랬고(투명 → 스크롤하면 `.on` 이
    // 흰 면을 깐다), 페이지 상단의 그라데이션이 헤더 뒤로 그대로 비쳐야 한다.
    // 글자만 바로 흰색으로 뒤집는다 — 뒤에 깔린 페이지가 이미 짙기 때문이다.
    border-bottom-color: transparent;

    .headerRenewalNav {
      .nav-trigger,
      .nav-link {
        color: #fff;
      }

      .nav-item:hover > .nav-trigger,
      .nav-item.is-active > .nav-trigger,
      .nav-link:hover,
      .nav-link.is-active {
        color: $dark-header-accent;
      }
    }

    // 관리자에게만 보이는 크레딧. 다크 면에서는 원래 다크인 대시보드 헤더의 크레딧과 같은
    // 모양으로 맞춘다(REQ-395) — 아이콘은 짙은 남색 칸의 회색 다이아몬드, 숫자와
    // "자세히 보기"는 위 메뉴의 활성 색, 라벨은 헤더의 다른 글자와 같은 흰색.
    //
    // 상단 바(.rightMenu)에만 건다. 모바일 드로어도 이 헤더 안에 있는데 흰 면이라,
    // `.infoBtn` 전체에 흰 글자를 주면 드로어의 "크레딧" 라벨이 사라진다.
    // 메가패널이 열려 헤더가 흰 면으로 돌아가는 동안에도 빼서 `_header.scss` 의
    // 밝은 면 모양을 그대로 쓴다.
    &:not(.is-nav-open) .rightMenu .infoBtn {
      color: #fff;

      // 오른쪽 구분선도 같이 올린다. 밝은 면 기준 색이라 다크에서는 보이지 않는다.
      &::before {
        background: rgba(255, 255, 255, 0.24);
      }

      // 값은 `_headerDashboard.scss` 의 `.infoBtn .icon` 과 같다. 아이콘 색은 대시보드용
      // 다이아몬드(UserCommonCreditInfo 의 `isMain` 이 아닌 쪽)가 실제로 그리는 색으로,
      // 대시보드 헤더가 같은 회색에 쓰는 토큰이다.
      .icon {
        border-color: rgba(11, 51, 161, 0.10);
        background: rgba(11, 51, 161, 0.75);

        path { fill: $color-txt-l-1; }
      }

      // `.btnTxt` 색은 전역 `_button.scss` 가 `!important` 로 박아 둔다 —
      // 대시보드 헤더(`_headerDashboard.scss`)처럼 `!important` 로 맞받는다.
      span,
      .btnTxt {
        color: $dark-header-accent !important;
      }

      .btnTxt::before {
        border-left-color: $dark-header-accent;
      }
    }

    // 메가패널은 흰 면이므로, 열리는 동안에는 헤더도 흰 면으로 돌아가 한 덩어리가 된다
    // (renewal 이 `.header.is-nav-open` 에서 하는 것과 같은 의도). 위의 밝은 면 규칙처럼 헤더 아래
    // 패널이 붙는 1081px 이상에서만 — 1080px 이하 드로어의 아코디언에는 걸지 않는다.
    @media (min-width: 1081px) {
      &.is-nav-open {
        background: #fff;

        .headerRenewalNav {
          .nav-trigger,
          .nav-link {
            color: #000;
          }

          .nav-item:hover > .nav-trigger,
          .nav-item.is-active > .nav-trigger,
          .nav-link:hover,
          .nav-link.is-active {
            color: $color-main;
          }
        }
      }
    }
  }

  // 스크롤해서 헤더가 콘텐츠 위에 떠 있을 때만 면을 깐다. 기본값은 흰 면인데
  // (_header.scss) 다크 페이지에서는 그 흰 면만 동떨어져 보이므로 페이지 맨 윗부분과
  // 같은 색으로 바꾼다.
  //
  // 스크롤 표시 `.on` 은 헤더가 아니라 래퍼(HomeCommonLayout 의 `.headerWrap`)에 붙는다.
  // 래퍼 없이 헤더만 쓰는 화면을 위해 `_header.scss` 는 `.headerMain.on` 도 함께 두고
  // 있으므로 여기서도 둘 다 받는다.
  //
  // 패널이 열려 있을 때는 빼 둔다 — 그때는 위의 `.is-nav-open` 규칙대로 패널과 같은
  // 흰 면이어야 한다.
  .headerWrap.on .headerMain:not(.is-nav-open),
  .headerMain.on:not(.is-nav-open) {
    background: $dark-header-bg;
    border-bottom-color: transparent;
  }

  // 1080px 이하에서 우리 헤더는 맨 위에서도 면을 깐다 — _header.scss 의 흰 면이고, 그래서 이
  // 폭에서는 콘텐츠를 헤더 뒤로 넣지 않는다(RenewalContent). 다크 페이지에서는 그 흰 면만 동떨어져
  // 보이고, 그 위의 다크용 흰 글자가 묻혀 메뉴가 보이지 않았다 — 스크롤과 상관없이 페이지 맨
  // 윗부분과 같은 짙은 면을 깐다.
  // 흰 면으로 바꾸는 위의 `.is-nav-open` 규칙은 1081px 이상(헤더 아래 붙는 데스크톱 메가패널)에서만
  // 걸리므로, 드로어 안의 아코디언을 펴도(nav-menu.js 가 그때도 `is-nav-open` 을 단다) 짙은 면이 유지된다.
  @include mq(screen-1080) {
    .headerMain {
      background: $dark-header-bg;

      // 햄버거(≤1080px)도 흰 선으로 — 검은 선(_header.scss)은 짙은 면에 묻힌다(REQ-398).
      // 펼친 상태(.on, X 모양)의 가운데 선은 투명이어야 하므로 접힌 상태에서만 칠한다.
      .ham {
        span::before,
        span::after {
          background: $color-white;
        }

        &:not(.on) span {
          background: $color-white;
        }
      }
    }
  }
}
</style>